import { AnyAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { usageClient } from '../../../api/client';
import { QueryUsageSummary } from '../../../api/schema';
import { getPagination, keysToId } from '../../../helpers/sliceHelper';
import { RootState } from '../../store';
import { UsageEventsType, UsageListQuery, UsageListType, UsageState, UsageSummaryQuery } from './usageInterface';

const eventsAdapter = createEntityAdapter<UsageListType>();

export const getUsageSummary = createAsyncThunk('usage/getUsageSummary', async (query: UsageSummaryQuery) =>
  usageClient.getUsageSummary(query.since, query.userType, query.organizationId, query.includeSelf),
);

export const listUsage = createAsyncThunk('usage/listUsage', async (query: UsageListQuery) =>
  usageClient.listUsage(query.since, query.userType, query.organizationId, query.includeSelf, query.search, query.sortBy, query.offset, query.limit),
);

export const usageSlice = createSlice({
  name: 'usage',
  initialState: {
    events: eventsAdapter.getInitialState(),
  } as UsageState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getUsageSummary.pending, (state: UsageState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getUsageSummary: true };
    });
    builder.addCase(getUsageSummary.fulfilled, (state: UsageState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getUsageSummary: false };
      state.summary = action.payload.data?.result;
    });
    builder.addCase(getUsageSummary.rejected, (state: UsageState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getUsageSummary: false };
    });
    builder.addCase(listUsage.pending, (state: UsageState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listUsage: true };
    });
    builder.addCase(listUsage.fulfilled, (state: UsageState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listUsage: false };
      if (action.payload.data?.result) {
        eventsAdapter.upsertOne(state.events, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listUsage.rejected, (state: UsageState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listUsage: false };
    });
  },
});

export const { selectById: selectUsageEventsById } = eventsAdapter.getSelectors((state: RootState) => state.usage.events);

const selectUsageEvents = createSelector([selectUsageEventsById], (events) => ({
  pagination: getPagination(events),
  result: events?.result,
}));

export const getUsageEvents =
  (keys: UsageListQuery) =>
  (state: RootState): UsageEventsType =>
    selectUsageEvents(state, keysToId(keys));

export const getUsageSummaryData = (state: RootState): QueryUsageSummary | undefined => state.usage.summary;
export const usageSummaryFetching = (state: RootState): boolean => state.usage.fetching?.getUsageSummary !== false;
export const usageEventsFetching = (state: RootState): boolean => state.usage.fetching?.listUsage !== false;

export default usageSlice.reducer;
